/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import React from 'react';
import { decorate, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { Icon } from 'semantic-ui-react';
import uuidv4 from 'uuid/v4';
import { displayWarning } from '@amzn/base-ui/dist/helpers/notification';

const iconStyle = { lineHeight: 1, height: '1em', margin: 0 };

/**
 * A reusable file input component.
 * Motivation: <input type="file" /> components are stateful and behave unexpectedly
 * when attempting to reuse them to upload multiple files.
 */
const ReusableFileInput = React.forwardRef(({ onChange, ...props }, ref) => {
  const [inputKey, setInputKey] = React.useState(uuidv4());
  return (
    <input
      key={inputKey}
      ref={ref}
      type="file"
      onChange={event => {
        onChange(event);
        setInputKey(uuidv4());
      }}
      {...props}
    />
  );
});

class FileDropZone extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.highlighted = false;
    });
  }

  setHighlight(isHighlighted) {
    runInAction(() => {
      this.highlighted = isHighlighted;
    });
  }

  handleSelectingFiles = event => {
    if (this.props.onSelectFiles) {
      const fileList = event.currentTarget.files || [];
      if (fileList.length > this.props.maximumUploadFilesLimit) {
        displayWarning(
          `There are currently ${fileList.length} files selected. Please select less than ${this.props.maximumUploadFilesLimit} files.`,
        );
      } else {
        this.props.onSelectFiles([...fileList]);
      }
    }
  };

  render() {
    const fileInputRef = React.createRef();
    const folderInputRef = React.createRef();
    const enabled = this.props.state === 'PENDING';
    const highlighted = this.highlighted;

    return (
      <div
        className={`upload-dropzone${highlighted ? ' upload-dropzone-active' : ''}`}
        onDragEnter={event => {
          if (enabled && event.dataTransfer.types.includes('Files')) {
            event.preventDefault();
            this.setHighlight(true);
          }
        }}
        onDragOver={event => {
          if (enabled && event.dataTransfer.types.includes('Files')) {
            event.preventDefault();
            this.setHighlight(true);
          }
        }}
        onDragLeave={() => this.setHighlight(false)}
        onDragEnd={() => this.setHighlight(false)}
        onDrop={event => {
          if (enabled && event.dataTransfer.types.includes('Files')) {
            event.preventDefault();
            this.setHighlight(false);
            const fileList = event.dataTransfer.files || [];
            this.props.onSelectFiles([...fileList]);
          }
        }}
      >
        <ReusableFileInput
          ref={fileInputRef}
          hidden
          multiple
          onChange={this.handleSelectingFiles}
        />
        <ReusableFileInput
          ref={folderInputRef}
          hidden
          multiple
          directory=""
          webkitdirectory=""
          onChange={this.handleSelectingFiles}
        />

        {this.props.state === 'PENDING' && (
          <>
            <div className="upload-dropzone-icon">
              <Icon name="cloud upload" style={{ ...iconStyle, fontSize: '2em' }} />
            </div>
            <div className="upload-dropzone-title">
              Drag and drop files here
            </div>
            <div className="upload-dropzone-subtitle">
              Maximum 5GB or 100 files
            </div>
            <div className="upload-dropzone-divider">
              <span>or</span>
            </div>
            <div className="upload-dropzone-buttons">
              <button
                className="workspace-action-btn workspace-action-btn-primary"
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
              >
                <Icon name="file" style={iconStyle} /> Select Files
              </button>
              <button
                className="workspace-action-btn workspace-action-btn-secondary"
                onClick={() => folderInputRef.current && folderInputRef.current.click()}
              >
                <Icon name="folder open" style={iconStyle} /> Select Folder
              </button>
            </div>
            <div className="upload-dropzone-hint">
              Folders must contain less than 100 files.
            </div>
          </>
        )}

        {this.props.state === 'UPLOADING' && (
          <>
            <div className="upload-dropzone-icon upload-dropzone-icon-active">
              <Icon name="spinner" loading style={{ ...iconStyle, fontSize: '2em' }} />
            </div>
            <div className="upload-dropzone-title">Uploading...</div>
          </>
        )}

        {this.props.state === 'COMPLETE' && (
          <>
            <div className="upload-dropzone-icon upload-dropzone-icon-success">
              <Icon name="check circle" style={{ ...iconStyle, fontSize: '2em' }} />
            </div>
            <div className="upload-dropzone-title">Upload Complete</div>
          </>
        )}
      </div>
    );
  }
}
FileDropZone.propTypes = {
  maximumUploadFilesLimit: PropTypes.isRequired,
  state: PropTypes.oneOf(['PENDING', 'UPLOADING', 'COMPLETE']).isRequired,
  onSelectFiles: PropTypes.func,
};
FileDropZone.defaultProps = {
  onSelectFiles: null,
};

decorate(FileDropZone, {
  setHighlight: observable,
});
export default observer(FileDropZone);
